// @ts-strict-ignore
import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { bindingsDefinition, injected } from '@/hybrid/core/bindings.util';
import { angularComponent } from '@/hybrid/core/react2angular.util';
import { useTranslation } from 'react-i18next';
import {
  DatasourcesService,
  DatasourceStatus,
  FilterParameters,
} from '@/hybrid/administration/datasources/datasources.service';
import { Overlay, Popover } from 'react-bootstrap';
import { DatasourcesStatusOutputV1, DatasourceSummaryStatusOutputV1 } from '@/sdk';
import { useInjectedBindings } from '@/hybrid/core/hooks/useInjectedBindings.hook';
import { Icon } from '@/hybrid/core/Icon.atom';
import { HoverTooltip } from '@/hybrid/core/HoverTooltip.atom';
import { useIntervalWhen } from 'rooks';
import { isAdmin } from '@/services/authorization.service';

const datasourcesStatusBindings = bindingsDefinition({
  sqDatasourcesService: injected<DatasourcesService>(),
});

export const DatasourcesStatus: SeeqComponent<typeof datasourcesStatusBindings> = () => {
  const { sqDatasourcesService } = useInjectedBindings(datasourcesStatusBindings);

  const [popupShown, setPopupShown] = useState(false);
  const { t } = useTranslation();
  const [datasourcesContainer, setDatasourcesContainer] = useState(null);
  const [fetchErrors, setFetchErrors] = useState(false);
  const target = useRef(null);

  const fetchDatasources = () => {
    sqDatasourcesService
      .fetchDatasourcesImmediately()
      .then((datasourcesStatus) => {
        setDatasourcesContainer(datasourcesStatus);
        setFetchErrors(false);
      })
      .catch(() => setFetchErrors(true));
  };

  useEffect(() => {
    if (popupShown) {
      fetchDatasources();
    }
  }, [popupShown]);

  useIntervalWhen(fetchDatasources, 2000, popupShown);

  const datasourcesStatusIcons = new Map([
    [DatasourceStatus.Unknown, 'fa-exclamation-triangle sq-text-danger'],
    [DatasourceStatus.Error, 'fa-exclamation-triangle sq-text-danger'],
    [DatasourceStatus.Warning, 'fa-exclamation-circle sq-text-warning'],
    [DatasourceStatus.Indexing, 'fa-refresh fa-spin'],
    [DatasourceStatus.Happy, 'fa-check sq-text-success'],
  ]);

  const datasourcesStatusMessages = new Map([
    [DatasourceStatus.Unknown, 'DATASOURCES.DISCONNECTED'],
    [DatasourceStatus.Error, 'DATASOURCES.DISCONNECTED'],
    [DatasourceStatus.Warning, 'DATASOURCES.INDEX_SUMMARY'],
    [DatasourceStatus.Indexing, 'DATASOURCES.INDEX_PROGRESS'],
    [DatasourceStatus.Happy, 'DATASOURCES.INDEX_SUMMARY'],
    [DatasourceStatus.NotConnectable, 'DATASOURCES.DISABLED'],
  ]);

  const emptyFilters: FilterParameters = {
    name: '',
    datasourceClass: '',
    datasourceId: '',
    agentName: '',
    status: '',
  };

  const emptyDatasourceContainer: DatasourcesStatusOutputV1 = {
    connectedConnectionsCount: 0,
    connectedDatasourcesCount: 0,
    totalConnectionsCount: 0,
    totalDatasourcesCount: 0,
    datasources: [],
    agents: [],
  };

  const renderAdministrationLink = () => {
    if (isAdmin()) {
      return (
        <div className="text-center pt7">
          <a href="/administration" target="_blank">
            <span>{t('DATASOURCES.MANAGEMENT')}</span>
          </a>
        </div>
      );
    }
  };

  const renderDatasourcesTray = (dsContainer: DatasourcesStatusOutputV1) => {
    const datasourcesStatuses = new Map();
    _.forEach(dsContainer.datasources, (ds) =>
      datasourcesStatuses.set(ds, sqDatasourcesService.getDatasourceStatus(ds)),
    );

    const getDatasourceStatusMessages = (ds: DatasourceSummaryStatusOutputV1) => {
      const status = datasourcesStatuses.get(ds);
      const messagePlaceholder = datasourcesStatusMessages.get(status);
      const args = {};
      if (status === DatasourceStatus.Happy || status === DatasourceStatus.Warning) {
        args['count'] = sqDatasourcesService.getDatasourceItemsCount(ds);
      }
      return t(messagePlaceholder, args);
    };

    const renderDatasource = (ds: DatasourceSummaryStatusOutputV1) => {
      const status = datasourcesStatuses.get(ds);
      const icon = datasourcesStatusIcons.get(status);
      const message = getDatasourceStatusMessages(ds);
      return (
        <div className="fakeTableRow fakeTableStriped flexColumnContainer flexNoGrowNoShrink" key={ds.id}>
          <div className="width-300 p7 breakWord">
            {ds.datasourceClass}: {ds.name}
          </div>
          <div className="p7">
            <Icon icon={icon} type="inherit" />
            <span className="ml5">{message}</span>
          </div>
        </div>
      );
    };

    const renderDatasources = () => {
      const enabledDatasources = _.filter(
        dsContainer.datasources,
        (ds) => sqDatasourcesService.getDatasourceStatus(ds) !== DatasourceStatus.NotConnectable,
      );
      const filteredDatasources = sqDatasourcesService.filterAndSortDatasources(enabledDatasources, emptyFilters);
      return (
        <div className="flexRowContainer fakeTableWrapper" data-testid="datasourcesList">
          {_.map(filteredDatasources, renderDatasource)}
        </div>
      );
    };

    const renderHeader = () => {
      return (
        <div className="flexColumnContainer fakeTableHeader">
          <div className="width-300 pt7 pr7 pb2 pl7">{t('NAME')}</div>
          <div className="nowrap pt7 pr7 pb2 pl7">{t('STATUS')}</div>
        </div>
      );
    };

    const datasourcesList = (
      <Overlay
        show={popupShown}
        target={target.current}
        rootClose={true}
        onHide={() => setPopupShown(false)}
        placement="top"
        transition={false}>
        <Popover id="dataSourcesPopup" className="popover datasourcesPopup min-width-500">
          <div className="flexRowContainer p10">
            {!fetchErrors && dsContainer.totalConnectionsCount !== 0 && renderHeader()}
            {!fetchErrors && dsContainer.totalConnectionsCount !== 0 && renderDatasources()}
            {fetchErrors && (
              <div className="flexColumnContainer sq-text-danger cursorPointer pt3 pb6">
                <Icon icon="fa-exclamation-triangle" type="danger" extraClassNames="flexCenter pl5 pr10 pt2" />
                <div className="flexFill pl5">{t('DATASOURCES.FETCH_ERROR')}</div>
              </div>
            )}
            {renderAdministrationLink()}
          </div>
        </Popover>
      </Overlay>
    );

    return (
      <div>
        <div
          className="sq-fairly-dark-gray mt0 mb0 cursorPointer"
          id="toggleDatasources"
          data-testid="datasourcesStatus"
          onClick={() => setPopupShown(!popupShown)}
          ref={target}>
          <HoverTooltip text={t('DATASOURCES.TOOLTIP')}>
            <span className="d-none d-sm-inline-block list-group-item-text ml3">{t('DATASOURCES.TITLE')}</span>
          </HoverTooltip>
        </div>
        {datasourcesList}
      </div>
    );
  };

  return datasourcesContainer
    ? renderDatasourcesTray(datasourcesContainer)
    : renderDatasourcesTray(emptyDatasourceContainer);
};

export const sqDatasourcesStatus = angularComponent(datasourcesStatusBindings, DatasourcesStatus);
